"use client";

import NextImage from "next/image";
import { useState } from "react";
import AppointmentBlock from "./AppointmentBlock";
import type { DomainConfig } from "../types";

/**
 * Gratis 3D-advies CTA-blok — geïmplementeerd uit de Claude Design handoff:
 * landingspaginas/project/3d-advies-blok/AdviesBlok.jsx
 *
 * Vier variants, pixel-perfect uit de design source:
 *   1 · "rustig-split"  — cream split, copy links + 3D rechts
 *   2 · "editorial-dark" — donkere bg, oranje accent, sketch groot rechts
 *   3 · "stappenflow"    — sketch als hero, 3 stappen onder
 *   4 · "tekentafel"     — graph-paper bg, sketch met hoek-markeringen + specs
 *
 * CTA opent default een MODAL met de afspraak-form (AppointmentBlock
 * variant="3d-ontwerp") erin — klant kan direct in de popup naam/email/
 * tel/datum/dagdeel invullen zonder van de pagina af te scrollen.
 *
 * Override via content.cta_mode = "link" als 't blok niet als CTA-trigger
 * moet werken maar gewoon als link (dan klikt 'ie naar content.cta_href).
 */

const ADV = {
  bg: "#F1EEE7",
  // Dark variant achtergrond: host-themed.
  // Zwaluw → --sage = #38423F (zw-blue-900). BW → fallback #1A1F26 (donkernavy).
  bgDark: "var(--sage, #1A1F26)",
  bgWarm: "#EDE3D0",
  card: "#FFFFFF",
  ink: "#141C28",
  inkSoft: "#2A333F",
  muted: "#5A6578",
  border: "#E4DED3",
  // CTA-kleur: host-themed via --terracotta.
  // Zwaluw → #D96A2A. BW → fallback #FF6B1A.
  cta: "var(--terracotta, #FF6B1A)",
};

export interface Adv3DBlockContent {
  /** Image URL voor de 3D-sketch. Default: /badkamer-3d-sketch.jpg (uit public/) */
  image_src?: string;
  /** "modal" (default): CTA opent popup met afspraak-form.
   *  "link": CTA werkt als gewone link naar `cta_href`. */
  cta_mode?: "modal" | "link";
  /** Alleen relevant als cta_mode='link'. Default: #appointment-block */
  cta_href?: string;
  /** CTA-label — alle 4 variants tonen 'm op de oranje knop */
  cta_label?: string;
  /** Optionele override voor het telefoonnummer in variant 3 */
  phone?: string;
  /** Variant 3 stap-content overschrijven */
  steps?: Array<{ n: string; t: string; s: string }>;
}

export type Adv3DBlockVariant =
  | "rustig-split"
  | "editorial-dark"
  | "stappenflow"
  | "tekentafel";

interface Props {
  content: Adv3DBlockContent;
  variant?: Adv3DBlockVariant;
  domain: DomainConfig;
  niche?: string;
}

const DEFAULT_IMAGE_SRC = "/badkamer-3d-sketch.jpg";
// Default klikt naar de afspraak-form op dezelfde pagina (AppointmentBlock
// rendert met id="appointment-block"). Klant flow: bezoeker ziet 3D-banner
// → klikt CTA → scrollt naar form → vult naam/email/tel/datum/dagdeel in.
// Override via content.cta_href als 'ie naar de brochure-form moet
// (#brochure-form) of een andere pagina (bv. /afspraak).
const DEFAULT_CTA_HREF = "#appointment-block";
const DEFAULT_CTA_LABEL = "Plan uw gratis 3D-advies";

// ─────────── Iconen ───────────────────────────────────────────────────────

function ArrowRight3D({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function CubeIcon({ size = 22, strokeWidth = 1.6 }: { size?: number; strokeWidth?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  );
}

function CheckIcon3D({ size = 16, strokeWidth = 2.4 }: { size?: number; strokeWidth?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="5 12 10 17 19 7" />
    </svg>
  );
}

function PencilIcon({ size = 18, strokeWidth = 1.6 }: { size?: number; strokeWidth?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

// ─────────── SketchFrame — 3D-sketch in nette frame ────────────────────────

interface SketchFrameProps {
  src: string;
  /** Pixel-width op desktop (compact narrows automatically). */
  w?: number;
  ratio?: number;
  framed?: boolean;
  dark?: boolean;
  showCorners?: boolean;
  showBadge?: boolean;
  /** Plaatsnaam voor SEO-vriendelijke alt-text (bv. 'Enschede'). */
  city?: string;
}

function SketchFrame({
  src,
  w = 520,
  ratio = 1000 / 740,
  framed = true,
  dark = false,
  showCorners = false,
  showBadge = false,
  city,
}: SketchFrameProps) {
  const h = w / ratio;
  return (
    <div
      style={{
        width: w,
        height: h,
        position: "relative",
        flexShrink: 0,
        background: dark ? "rgba(255,255,255,0.04)" : ADV.card,
        padding: framed ? 10 : 0,
        border: framed
          ? `1px solid ${dark ? "rgba(255,255,255,0.1)" : ADV.border}`
          : "none",
        borderRadius: 4,
        boxShadow: framed
          ? dark
            ? "0 24px 48px -20px rgba(0,0,0,0.55)"
            : "0 22px 44px -22px rgba(20,28,40,0.22), 0 4px 10px -6px rgba(20,28,40,0.1)"
          : "none",
      }}
    >
      {/* Image — Next/Image met fill + sizes voor optimalisatie. Background-cover
          equivalent voor pixel-parity met 't prototype. */}
      <div
        style={{
          position: "absolute",
          inset: framed ? 10 : 0,
          backgroundColor: "#fff",
          overflow: "hidden",
        }}
      >
        <NextImage
          src={src}
          alt={city ? `3D-ontwerp van een badkamerrenovatie in ${city}` : "3D-ontwerp van een badkamer-renovatie"}
          fill
          sizes={`${Math.round(w)}px`}
          style={{ objectFit: "cover", objectPosition: "center" }}
        />
      </div>

      {/* Hoek-markeringen (variant 4) */}
      {showCorners ? (
        <>
          {[
            { top: -6, left: -6, b: { borderTop: 2, borderLeft: 2 } },
            { top: -6, right: -6, b: { borderTop: 2, borderRight: 2 } },
            { bottom: -6, left: -6, b: { borderBottom: 2, borderLeft: 2 } },
            { bottom: -6, right: -6, b: { borderBottom: 2, borderRight: 2 } },
          ].map((c, i) => {
            const { b, ...pos } = c;
            const borderStyle: Record<string, string> = {};
            for (const [k, v] of Object.entries(b)) {
              borderStyle[k] = `${v}px solid ${ADV.cta}`;
            }
            return (
              <div
                key={i}
                aria-hidden
                style={{
                  position: "absolute",
                  width: 18,
                  height: 18,
                  ...pos,
                  ...borderStyle,
                }}
              />
            );
          })}
        </>
      ) : null}

      {/* Badge (variant 2) */}
      {showBadge ? (
        <div
          style={{
            position: "absolute",
            top: 18,
            left: 18,
            background: ADV.cta,
            color: "white",
            padding: "6px 12px",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            borderRadius: 2,
            boxShadow: "0 4px 10px -2px rgba(255,107,26,0.5)",
            fontFamily: "'Inter', monospace",
          }}
        >
          Voorbeeld 3D-ontwerp
        </div>
      ) : null}
    </div>
  );
}

// ─────────── Hook: detecteer mobile via window.matchMedia ──────────────────
// We renderen server-side per default als desktop, maar laten 't door CSS
// breakpoints regelen via media-queries voor de outer wrapper. Concreet:
// elke variant gebruikt een 'compact' boolean dat we door CSS @media regelen.
// Voor SSR-veiligheid: we forceren server-render = desktop, en CSS-only
// overrides regelen de mobile-look.

const COMPACT_MQ = "@media (max-width: 768px)";

// ============================================================
// VARIANT 1 — Rustig split (cream)
// ============================================================

function Variant1({ src, ctaHref, ctaLabel, city }: { src: string; ctaHref: string; ctaLabel: string; city?: string }) {
  return (
    <section
      style={{
        width: "100%",
        background: ADV.bgWarm,
        padding: "56px 56px",
        borderTop: `1px solid ${ADV.border}`,
        borderBottom: `1px solid ${ADV.border}`,
        fontFamily: "system-ui, sans-serif",
      }}
      className="adv3d-v1"
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
            ${COMPACT_MQ} {
              .adv3d-v1 { padding: 32px 20px !important; }
              .adv3d-v1__grid { grid-template-columns: 1fr !important; gap: 28px !important; }
              .adv3d-v1__copy { text-align: center !important; }
              .adv3d-v1__copy h3 { font-size: 30px !important; }
              .adv3d-v1__copy p { font-size: 14.5px !important; margin-left: auto !important; margin-right: auto !important; }
              .adv3d-v1__bullets { align-items: center !important; }
              .adv3d-v1__sketch-wrap { justify-content: center !important; }
              .adv3d-v1__sketch { width: 340px !important; height: ${340 / (1000 / 740)}px !important; }
            }
          `,
        }}
      />
      <div
        className="adv3d-v1__grid"
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr 1.15fr",
          gap: 56,
          alignItems: "center",
        }}
      >
        <div className="adv3d-v1__copy">
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 12px",
              borderRadius: 999,
              background: `${ADV.cta}18`,
              color: ADV.cta,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              marginBottom: 16,
            }}
          >
            <CubeIcon size={13} strokeWidth={2} /> 100% gratis
          </div>
          <h3
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: 44,
              fontWeight: 500,
              color: ADV.ink,
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
              margin: "0 0 14px",
            }}
          >
            Zie uw nieuwe badkamer
            <br />
            <span style={{ fontStyle: "italic" }}>vóór</span> we beginnen.
          </h3>
          <p
            style={{
              fontSize: 16,
              color: ADV.muted,
              lineHeight: 1.6,
              margin: "0 0 22px",
              maxWidth: 460,
            }}
          >
            Onze adviseur tekent uw badkamer in 3D — indeling, materialen, kleuren. Zo weet u
            precies wat u krijgt, voor er één tegel ligt.
          </p>

          <div
            className="adv3d-v1__bullets"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              alignItems: "flex-start",
              marginBottom: 22,
            }}
          >
            {[
              "Vrijblijvend, geen verplichting",
              "Aan huis of in onze showroom",
              "Inclusief materiaaladvies",
            ].map((t) => (
              <div
                key={t}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  fontSize: 14,
                  color: ADV.inkSoft,
                }}
              >
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: ADV.cta,
                    color: "white",
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <CheckIcon3D size={11} strokeWidth={3} />
                </div>
                {t}
              </div>
            ))}
          </div>

          <a
            href={ctaHref}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "15px 28px",
              borderRadius: 999,
              background: ADV.cta,
              color: "white",
              fontSize: 15,
              fontWeight: 600,
              textDecoration: "none",
              boxShadow: "0 14px 28px -14px rgba(255,107,26,0.55)",
            }}
          >
            {ctaLabel} <ArrowRight3D />
          </a>
        </div>

        <div
          className="adv3d-v1__sketch-wrap"
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <div className="adv3d-v1__sketch">
            <SketchFrame src={src} w={560} framed {...(city ? { city } : {})} />
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// VARIANT 2 — Editorial dark
// ============================================================

function Variant2({ src, ctaHref, ctaLabel, city }: { src: string; ctaHref: string; ctaLabel: string; city?: string }) {
  return (
    <section
      style={{
        width: "100%",
        background: ADV.bgDark,
        color: "#F1EEE7",
        padding: "60px 56px",
        fontFamily: "system-ui, sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
      className="adv3d-v2"
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
            ${COMPACT_MQ} {
              .adv3d-v2 { padding: 36px 20px !important; }
              .adv3d-v2__grid { grid-template-columns: 1fr !important; gap: 24px !important; }
              .adv3d-v2__copy { text-align: center !important; }
              .adv3d-v2__copy h3 { font-size: 32px !important; }
              .adv3d-v2__copy p { font-size: 14.5px !important; margin-left: auto !important; margin-right: auto !important; }
              .adv3d-v2__sketch-desktop { display: none !important; }
              .adv3d-v2__sketch-mobile { display: flex !important; }
            }
          `,
        }}
      />
      {/* Oranje gloed */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          right: "-10%",
          top: "10%",
          width: 700,
          height: 700,
          background: "radial-gradient(circle, rgba(255,107,26,0.14) 0%, transparent 60%)",
          pointerEvents: "none",
        }}
      />
      {/* Subtiele diagonale lijnen */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.04,
          backgroundImage:
            "repeating-linear-gradient(115deg, #F1EEE7 0 1px, transparent 1px 40px)",
        }}
      />

      <div
        className="adv3d-v2__grid"
        style={{
          position: "relative",
          maxWidth: 1200,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr 1.2fr",
          gap: 56,
          alignItems: "center",
        }}
      >
        <div className="adv3d-v2__copy">
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.22em",
              color: ADV.cta,
              textTransform: "uppercase",
              marginBottom: 14,
            }}
          >
            Gratis 3D-ontwerp
          </div>
          <h3
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: 52,
              fontWeight: 500,
              color: "#F1EEE7",
              lineHeight: 1,
              letterSpacing: "-0.02em",
              margin: "0 0 16px",
            }}
          >
            Eerst zien.
            <br />
            <span style={{ fontStyle: "italic", color: ADV.cta }}>Dan</span> beslissen.
          </h3>
          <p
            style={{
              fontSize: 16,
              lineHeight: 1.6,
              color: "rgba(241,238,231,0.72)",
              margin: "0 0 26px",
              maxWidth: 480,
            }}
          >
            U krijgt een 3D-tekening van uw badkamer — ingemeten op de centimeter, met de
            materialen die u kiest. Pas dan zetten we de schop in de grond.
          </p>

          {/* Mobile-only sketch — verschijnt boven de CTA op compact */}
          <div
            className="adv3d-v2__sketch-mobile"
            style={{
              display: "none",
              justifyContent: "center",
              marginBottom: 24,
            }}
          >
            <SketchFrame src={src} w={320} framed dark {...(city ? { city } : {})} />
          </div>

          <a
            href={ctaHref}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "16px 30px",
              borderRadius: 999,
              background: ADV.cta,
              color: "white",
              fontSize: 15,
              fontWeight: 600,
              textDecoration: "none",
              boxShadow: "0 14px 32px -10px rgba(255,107,26,0.55)",
              marginBottom: 14,
            }}
          >
            {ctaLabel} <ArrowRight3D />
          </a>
          <div
            style={{
              fontSize: 12,
              color: "rgba(241,238,231,0.5)",
              letterSpacing: "0.05em",
            }}
          >
            Vrijblijvend · geen verplichting
          </div>
        </div>

        <div
          className="adv3d-v2__sketch-desktop"
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <SketchFrame src={src} w={620} framed dark showBadge {...(city ? { city } : {})} />
        </div>
      </div>
    </section>
  );
}

// ============================================================
// VARIANT 3 — Stappenflow
// ============================================================

const DEFAULT_STEPS: Array<{ n: string; t: string; s: string }> = [
  { n: "01", t: "U vraagt aan", s: "Vul uw gegevens in — wij plannen een afspraak in." },
  { n: "02", t: "3D-ontwerp", s: "Onze adviseur tekent uw badkamer compleet in 3D." },
  { n: "03", t: "U kiest", s: "U beslist pas wanneer u 100% tevreden bent." },
];

function Variant3({
  src,
  ctaHref,
  ctaLabel,
  phone,
  steps,
  city,
}: {
  src: string;
  ctaHref: string;
  ctaLabel: string;
  phone: string;
  steps: Array<{ n: string; t: string; s: string }>;
  city?: string;
}) {
  return (
    <section
      style={{
        width: "100%",
        background: ADV.bg,
        padding: "56px 56px",
        fontFamily: "system-ui, sans-serif",
      }}
      className="adv3d-v3"
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
            ${COMPACT_MQ} {
              .adv3d-v3 { padding: 36px 20px !important; }
              .adv3d-v3__hero { height: 200px !important; }
              .adv3d-v3__inner { padding: 28px 24px !important; }
              .adv3d-v3__heading { font-size: 28px !important; }
              .adv3d-v3__sub { font-size: 14px !important; }
              .adv3d-v3__steps { grid-template-columns: 1fr !important; gap: 24px !important; }
              .adv3d-v3__line { display: none !important; }
              .adv3d-v3__step { text-align: left !important; display: grid !important; grid-template-columns: auto 1fr !important; gap: 16px !important; align-items: flex-start !important; }
              .adv3d-v3__step-circle { width: 48px !important; height: 48px !important; font-size: 17px !important; margin: 0 !important; }
              .adv3d-v3__step-title { font-size: 19px !important; }
              .adv3d-v3__step-body { font-size: 13.5px !important; max-width: none !important; margin-left: 0 !important; margin-right: 0 !important; }
              .adv3d-v3__cta-row { flex-direction: column !important; padding-top: 20px !important; }
            }
          `,
        }}
      />
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          background: ADV.card,
          border: `1px solid ${ADV.border}`,
          borderRadius: 18,
          overflow: "hidden",
          boxShadow: "0 24px 48px -28px rgba(20,28,40,0.18)",
        }}
      >
        {/* Hero met sketch als achtergrond */}
        <div
          className="adv3d-v3__hero"
          style={{
            position: "relative",
            height: 280,
            backgroundImage: `url('${src}')`,
            backgroundSize: "cover",
            backgroundPosition: "center 40%",
            borderBottom: `1px solid ${ADV.border}`,
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(255,255,255,0) 40%, rgba(255,255,255,0.92) 100%)",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: 32,
              top: 24,
              background: ADV.cta,
              color: "white",
              padding: "6px 12px",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
            }}
          >
            Voorbeeld 3D-ontwerp
          </div>
        </div>

        <div className="adv3d-v3__inner" style={{ padding: "40px 56px" }}>
          <div
            style={{
              textAlign: "center",
              marginBottom: 36,
              maxWidth: 620,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.22em",
                color: ADV.cta,
                textTransform: "uppercase",
                marginBottom: 12,
              }}
            >
              Zo werkt het
            </div>
            <h3
              className="adv3d-v3__heading"
              style={{
                fontFamily: "'Fraunces', serif",
                fontSize: 38,
                fontWeight: 500,
                color: ADV.ink,
                lineHeight: 1.1,
                letterSpacing: "-0.015em",
                margin: "0 0 10px",
              }}
            >
              Uw 3D-ontwerp in <span style={{ fontStyle: "italic" }}>drie stappen.</span>
            </h3>
            <p
              className="adv3d-v3__sub"
              style={{
                fontSize: 15.5,
                color: ADV.muted,
                lineHeight: 1.55,
                margin: 0,
              }}
            >
              Persoonlijk, op maat en geheel gratis — zonder verplichting.
            </p>
          </div>

          <div
            className="adv3d-v3__steps"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 28,
              marginBottom: 36,
              position: "relative",
            }}
          >
            <div
              className="adv3d-v3__line"
              aria-hidden
              style={{
                position: "absolute",
                top: 28,
                left: "16%",
                right: "16%",
                height: 1,
                borderTop: `2px dashed ${ADV.border}`,
                zIndex: 0,
              }}
            />
            {steps.map((step) => (
              <div
                key={step.n}
                className="adv3d-v3__step"
                style={{
                  position: "relative",
                  zIndex: 1,
                  textAlign: "center",
                }}
              >
                <div
                  className="adv3d-v3__step-circle"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: ADV.bgWarm,
                    border: `2px solid ${ADV.cta}`,
                    display: "grid",
                    placeItems: "center",
                    fontFamily: "'Fraunces', serif",
                    fontStyle: "italic",
                    fontSize: 19,
                    fontWeight: 500,
                    color: ADV.cta,
                    margin: "0 auto 16px",
                    flexShrink: 0,
                  }}
                >
                  {step.n}
                </div>
                <div>
                  <div
                    className="adv3d-v3__step-title"
                    style={{
                      fontFamily: "'Fraunces', serif",
                      fontSize: 21,
                      fontWeight: 500,
                      color: ADV.ink,
                      lineHeight: 1.2,
                      letterSpacing: "-0.01em",
                      margin: "0 0 6px",
                    }}
                  >
                    {step.t}
                  </div>
                  <div
                    className="adv3d-v3__step-body"
                    style={{
                      fontSize: 14,
                      color: ADV.muted,
                      lineHeight: 1.5,
                      maxWidth: 240,
                      marginLeft: "auto",
                      marginRight: "auto",
                    }}
                  >
                    {step.s}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div
            className="adv3d-v3__cta-row"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "row",
              gap: 14,
              paddingTop: 28,
              borderTop: `1px solid ${ADV.border}`,
            }}
          >
            <a
              href={ctaHref}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                padding: "15px 28px",
                borderRadius: 999,
                background: ADV.cta,
                color: "white",
                fontSize: 15,
                fontWeight: 600,
                textDecoration: "none",
                boxShadow: "0 12px 24px -12px rgba(255,107,26,0.55)",
              }}
            >
              {ctaLabel} <ArrowRight3D />
            </a>
            {phone ? (
              <div style={{ fontSize: 13, color: ADV.muted }}>
                Of bel ons direct:{" "}
                <a
                  href={`tel:${phone.replace(/\s/g, "")}`}
                  data-phone-source="adv-3d-v3"
                  style={{ color: ADV.ink, fontWeight: 600, textDecoration: "none" }}
                >
                  {phone}
                </a>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// VARIANT 4 — Tekentafel
// ============================================================

function Variant4({ src, ctaHref, ctaLabel, city }: { src: string; ctaHref: string; ctaLabel: string; city?: string }) {
  return (
    <section
      style={{
        width: "100%",
        background: `radial-gradient(circle at 80% 20%, rgba(255,107,26,0.06) 0%, transparent 50%), ${ADV.bg}`,
        padding: "56px 56px",
        fontFamily: "system-ui, sans-serif",
        borderTop: `1px solid ${ADV.border}`,
        borderBottom: `1px solid ${ADV.border}`,
        position: "relative",
        overflow: "hidden",
      }}
      className="adv3d-v4"
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
            ${COMPACT_MQ} {
              .adv3d-v4 { padding: 36px 20px !important; }
              .adv3d-v4__grid { grid-template-columns: 1fr !important; gap: 32px !important; }
              .adv3d-v4__copy { text-align: center !important; }
              .adv3d-v4__copy h3 { font-size: 30px !important; }
              .adv3d-v4__copy p { font-size: 14.5px !important; margin-left: auto !important; margin-right: auto !important; }
              .adv3d-v4__specs { max-width: 320px !important; margin-left: auto !important; margin-right: auto !important; }
              .adv3d-v4__sketch { width: 340px !important; height: ${340 / (1000 / 740)}px !important; }
            }
          `,
        }}
      />
      {/* Tekenpapier-grid achtergrond */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          backgroundImage: `
            linear-gradient(rgba(20,28,40,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(20,28,40,0.04) 1px, transparent 1px)
          `,
          backgroundSize: "32px 32px",
        }}
      />

      <div
        className="adv3d-v4__grid"
        style={{
          position: "relative",
          maxWidth: 1200,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1.2fr 1fr",
          gap: 48,
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "20px 0",
            position: "relative",
          }}
        >
          <div className="adv3d-v4__sketch">
            <SketchFrame src={src} w={560} framed showCorners {...(city ? { city } : {})} />
          </div>
        </div>

        <div className="adv3d-v4__copy">
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 12px",
              background: ADV.card,
              border: `1px dashed ${ADV.cta}`,
              borderRadius: 2,
              color: ADV.cta,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              marginBottom: 16,
              fontFamily: "'Inter', monospace",
            }}
          >
            <PencilIcon size={12} strokeWidth={2} /> Op maat · op schaal
          </div>
          <h3
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: 44,
              fontWeight: 500,
              color: ADV.ink,
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
              margin: "0 0 14px",
            }}
          >
            Een 3D-tekening van
            <br />
            <span style={{ fontStyle: "italic", color: ADV.cta }}>úw</span> badkamer.
          </h3>
          <p
            style={{
              fontSize: 16,
              color: ADV.muted,
              lineHeight: 1.6,
              margin: "0 0 18px",
              maxWidth: 460,
            }}
          >
            Niet zomaar een voorbeeldje — uw écht badkamer, ingemeten op de centimeter, met de
            materialen die u kiest. Persoonlijk, gratis, vrijblijvend.
          </p>

          <div
            className="adv3d-v4__specs"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              padding: "16px 0",
              borderTop: `1px dashed ${ADV.border}`,
              borderBottom: `1px dashed ${ADV.border}`,
              marginBottom: 22,
              fontFamily: "'Inter', monospace",
              fontSize: 12,
              color: ADV.muted,
              textAlign: "left",
            }}
          >
            <div>
              <div
                style={{
                  color: ADV.cta,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  fontSize: 10,
                  marginBottom: 4,
                }}
              >
                — Doorlooptijd
              </div>
              <div
                style={{
                  color: ADV.ink,
                  fontWeight: 600,
                  fontSize: 14,
                  fontFamily: "system-ui",
                }}
              >
                Binnen 5 werkdagen
              </div>
            </div>
            <div>
              <div
                style={{
                  color: ADV.cta,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  fontSize: 10,
                  marginBottom: 4,
                }}
              >
                — Locatie
              </div>
              <div
                style={{
                  color: ADV.ink,
                  fontWeight: 600,
                  fontSize: 14,
                  fontFamily: "system-ui",
                }}
              >
                Bij u thuis of showroom
              </div>
            </div>
          </div>

          <a
            href={ctaHref}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "15px 28px",
              borderRadius: 999,
              background: ADV.cta,
              color: "white",
              fontSize: 15,
              fontWeight: 600,
              textDecoration: "none",
              boxShadow: "0 14px 28px -14px rgba(255,107,26,0.55)",
            }}
          >
            {ctaLabel} <ArrowRight3D />
          </a>
        </div>
      </div>
    </section>
  );
}

// ─────────── Modal wrapper ─────────────────────────────────────────────────

interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

function CtaModal({ open, onClose, children }: ModalProps) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,28,40,0.55)",
        display: "grid",
        placeItems: "center",
        padding: 8,
        zIndex: 10000,
        overflowY: "auto",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      {/* Mobile: minder padding zodat het form niet uit het scherm valt. */}
      <style>{`
        @media (max-width: 480px) {
          .bw-cta-modal-card { padding: 24px 18px !important; border-radius: 12px !important; }
        }
      `}</style>
      <div
        className="bw-cta-modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          background: "#FFFFFF",
          borderRadius: 16,
          width: "100%",
          maxWidth: 760,
          padding: "36px 32px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
          maxHeight: "94vh",
          overflowY: "auto",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Sluiten"
          style={{
            position: "absolute",
            top: 10,
            right: 12,
            width: 36,
            height: 36,
            border: "none",
            background: "transparent",
            fontSize: 28,
            lineHeight: 1,
            cursor: "pointer",
            color: "#5A6578",
            zIndex: 1,
          }}
        >
          ×
        </button>
        {children}
      </div>
    </div>
  );
}

// ─────────── Public block-component ────────────────────────────────────────

export default function Adv3DBlock({ content, variant = "rustig-split", domain, niche }: Props) {
  const src = content.image_src ?? DEFAULT_IMAGE_SRC;
  const ctaHref = content.cta_href ?? DEFAULT_CTA_HREF;
  const ctaLabel = content.cta_label ?? DEFAULT_CTA_LABEL;
  const phone = content.phone ?? "";
  const steps = content.steps ?? DEFAULT_STEPS;
  const ctaMode = content.cta_mode ?? "modal";
  const city = domain.page_city;

  const [modalOpen, setModalOpen] = useState(false);

  // Wanneer cta_mode='modal': click op een <a href={ctaHref}>...</a> binnen
  // de variant onderscheppen we via event-bubbling. Anders werkt 't als
  // gewone link (anchor of externe URL).
  const onWrapperClick = (e: React.MouseEvent) => {
    if (ctaMode !== "modal") return;
    const target = (e.target as HTMLElement).closest("a") as HTMLAnchorElement | null;
    if (!target) return;
    if (target.getAttribute("href") !== ctaHref) return;
    e.preventDefault();
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  let variantNode: React.ReactNode;
  switch (variant) {
    case "editorial-dark":
      variantNode = <Variant2 src={src} ctaHref={ctaHref} ctaLabel={ctaLabel} {...(city ? { city } : {})} />;
      break;
    case "stappenflow":
      variantNode = (
        <Variant3
          src={src}
          ctaHref={ctaHref}
          ctaLabel={ctaLabel}
          phone={phone}
          steps={steps}
          {...(city ? { city } : {})}
        />
      );
      break;
    case "tekentafel":
      variantNode = <Variant4 src={src} ctaHref={ctaHref} ctaLabel={ctaLabel} {...(city ? { city } : {})} />;
      break;
    case "rustig-split":
    default:
      variantNode = <Variant1 src={src} ctaHref={ctaHref} ctaLabel={ctaLabel} {...(city ? { city } : {})} />;
      break;
  }

  return (
    <>
      <div onClick={onWrapperClick}>{variantNode}</div>
      {ctaMode === "modal" ? (
        <CtaModal open={modalOpen} onClose={closeModal}>
          <AppointmentBlock
            content={{}}
            variant="3d-ontwerp"
            domain={domain}
            {...(niche ? { niche } : {})}
            embedded
          />
        </CtaModal>
      ) : null}
    </>
  );
}
